import * as msal from '@azure/msal-browser';
import { CompletionCriterion } from '@beeboard/common';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-client';
import { defineStore } from 'pinia';
import { useIoStore, type MapActivity, type MapTask } from '@/store';
import { useActivityProjectStore } from '@/store/activityProjectStore';
import {
  checkAuthentication as msCheckAuth,
  createGraphClient,
  createMsalConfig,
  login as msLogin,
  htmlToPlainText,
} from '@/utils/helpers/msGraphHelpers';
import { getRedirectURI, getTenant } from '@/utils/helpers/urlManipulation';
import { shortenString } from '@/utils/helpers/various';

const subdomain = getTenant();
const tenantId = import.meta.env[`VITE_${subdomain}_MSTODO_TENANT_ID`];
const clientId = import.meta.env[`VITE_${subdomain}_MSTODO_CLIENT_ID`];
const config = createMsalConfig(clientId, tenantId, getRedirectURI());
const msalInstance = new msal.PublicClientApplication(config);

export type User = {
  id: string;
  displayName: string;
  mail: string;
};

export type TodoList = {
  id: string;
  displayName: string;
  isOwner: boolean;
  isShared: boolean;
};

export type TodoTask = {
  id: string;
  title: string;
  status:
    | 'notStarted'
    | 'inProgress'
    | 'completed'
    | 'waitingOnOthers'
    | 'deferred';
  importance: 'low' | 'normal' | 'high';
  body?: {
    content: string;
    contentType: 'text' | 'html';
  };
  dueDateTime?: {
    dateTime: string;
    timeZone: string;
  };
};

export type ChecklistItem = {
  id?: string;
  displayName: string;
  isChecked?: boolean;
};

// Task types for actions and completion criteria from Beeboard
export interface TaskItem {
  id: string;
  title: string;
  state: 'planned' | 'in_progress' | 'done' | string;
  current_deadline?: string;
}

export interface ActivitySummary {
  id: string;
  title: string;
}

export type TodoTaskCreateRequest = {
  title: string;
  body?: {
    content: string;
    contentType: 'text' | 'html';
  };
  dueDateTime?: {
    dateTime: string;
    timeZone: string;
  };
  importance?: 'low' | 'normal' | 'high';
  status?:
    | 'notStarted'
    | 'inProgress'
    | 'completed'
    | 'waitingOnOthers'
    | 'deferred';
};

export type TodoStoreState = {
  scopes: Array<string>;
  user: User | null;
  todoLists: Array<TodoList>;
  todoTasks: Array<TodoTask>;
  isExporting: boolean;
  isImporting: boolean;
  _graphClient: MicrosoftGraph.Client | null;
};

// Helper functions for common operations
function createActivityTaskRequest(
  activity: MapActivity,
  mapId: string,
): TodoTaskCreateRequest {
  const subdomain = getTenant();

  return {
    title: shortenString(
      `${$t('msPlannerIntegration.activityShort')}${activity.sequence} ${activity.title}`,
    ),
    body: {
      content: `${$t('msTodoIntegration.taskBodyActivity', { title: activity.title })}\n\n${$t('msTodoIntegration.taskBodyViewInBeeboard', { url: `https://${subdomain}.beeboard.eu/projects/${mapId}/activities/${activity.id}` })}`,
      contentType: 'text',
    },
    dueDateTime: activity.deadline
      ? {
          dateTime: activity.deadline,
          timeZone: 'UTC',
        }
      : undefined,
    importance: 'normal',
    status: 'notStarted',
  };
}

function createCriterionTaskRequest(
  criterion: CompletionCriterion,
  activity: MapActivity,
  mapId: string,
  number: number,
): TodoTaskCreateRequest {
  const subdomain = getTenant();

  return {
    title: shortenString(
      `${$t('msPlannerIntegration.completionCriterionShort')}${activity.sequence}.${number} ${criterion.title}`,
    ),
    body: {
      content: `${$t('msTodoIntegration.taskBodyCompletionCriterion')}\n${$t('msTodoIntegration.taskBodyActivityLabel', { title: activity.title })}\n\n${$t('msTodoIntegration.taskBodyViewInBeeboard', { url: `https://${subdomain}.beeboard.eu/projects/${mapId}/activities/${activity.id}/completion-criteria/${criterion.id}` })}`,
      contentType: 'text',
    },
    dueDateTime: criterion.manual_due_date
      ? {
          dateTime: criterion.current_deadline,
          timeZone: 'UTC',
        }
      : activity.deadline
        ? {
            dateTime: activity.deadline,
            timeZone: 'UTC',
          }
        : undefined,
    importance: 'normal',
    status: criterion.state === 'done' ? 'completed' : 'notStarted',
  };
}

async function addChecklistItemsToTask(
  graphClient: MicrosoftGraph.Client,
  listId: string,
  taskId: string,
  criteria: Array<CompletionCriterion | MapTask>,
) {
  const activeCriteria = criteria.filter(
    (criterion) => criterion.state !== 'done',
  );

  for (const criterion of activeCriteria) {
    const checklistItem: ChecklistItem = {
      displayName: shortenString(criterion.title, 100),
      isChecked: false,
    };

    try {
      await graphClient
        .api(`/me/todo/lists/${listId}/tasks/${taskId}/checklistItems`)
        .post(checklistItem);
    } catch (error) {
      console.error('Failed to create checklist item:', error);
    }
  }
}

export const useTodoStore = defineStore('todoStore', {
  state: (): TodoStoreState => ({
    scopes: [
      'User.Read',
      'Tasks.ReadWrite', // Same scope works for both Planner and Todo
    ],
    user: null,
    todoLists: [],
    todoTasks: [],
    isExporting: false,
    isImporting: false,
    _graphClient: null,
  }),

  getters: {
    defaultList(state) {
      // Return the default "Tasks" list or first available list
      return (
        state.todoLists.find(
          (list) => list.displayName === 'Tasks' && list.isOwner,
        ) ||
        state.todoLists.find((list) => list.isOwner) ||
        state.todoLists[0]
      );
    },

    // Lazy singleton: create Graph client once and reuse
    graphClient(state): MicrosoftGraph.Client {
      if (!state._graphClient) {
        state._graphClient = createGraphClient(msalInstance, this.scopes);
      }

      return state._graphClient as MicrosoftGraph.Client;
    },
  },

  actions: {
    async checkAuthentication() {
      return msCheckAuth(msalInstance, this.scopes);
    },

    async login() {
      await msLogin(msalInstance, this.scopes);
    },

    async loadUserInfo() {
      try {
        this.user = await this.graphClient.api('/me').get();
      } catch (error) {
        console.error(error);
      }
    },

    async loadTodoLists() {
      try {
        const response = await this.graphClient.api('/me/todo/lists').get();
        this.todoLists = response.value || [];

        return this.todoLists;
      } catch (error) {
        console.error('Error loading Todo lists:', error);
        this.todoLists = [];
        throw error;
      }
    },

    async loadTodoTasks(listId: string): Promise<TodoTask[]> {
      try {
        const response = await this.graphClient
          .api(`/me/todo/lists/${listId}/tasks`)
          .top(200)
          .get();
        this.todoTasks = response.value || [];

        return this.todoTasks;
      } catch (error) {
        console.error('Error loading Todo tasks:', error);
        this.todoTasks = [];
        throw error;
      }
    },

    async createTodoList(displayName: string): Promise<TodoList | null> {
      try {
        const newList = await this.graphClient.api('/me/todo/lists').post({
          displayName: displayName,
        });

        // Reload lists to get updated list
        await this.loadTodoLists();

        return newList;
      } catch (error) {
        console.error(error);

        return null;
      }
    },

    async exportPhaseToTodo(
      selectedListId: string,
      selectedPhase: string,
      includeCriteriaAsChecklist: boolean = true,
    ) {
      const ioStore = useIoStore();
      const activities = ioStore
        .activitiesInPhase(selectedPhase)
        .filter((activity) => activity.state !== 'done')
        .sort((a, b) => {
          return b.deadline.localeCompare(a.deadline);
        });

      await this.exportActivitiesToTodo(
        selectedListId,
        activities,
        includeCriteriaAsChecklist,
      );
    },

    async exportSelectedActivitiesToTodo(
      selectedListId: string,
      selectedActivityIds: string[],
      includeCriteriaAsChecklist: boolean = true,
    ) {
      const ioStore = useIoStore();
      const activities = ioStore.activities
        .filter((activity) => selectedActivityIds.includes(activity.id))
        .filter((activity) => activity.state !== 'done')
        .sort((a, b) => {
          return b.deadline.localeCompare(a.deadline);
        });

      await this.exportActivitiesToTodo(
        selectedListId,
        activities,
        includeCriteriaAsChecklist,
      );
    },

    async exportSingleActivityToTodo(
      selectedListId: string,
      activity: MapActivity,
      includeCriteria: boolean = true,
    ) {
      const ioStore = useIoStore();
      const activityProjectStore = useActivityProjectStore();

      // Create main task for the activity using helper
      const taskRequest = createActivityTaskRequest(activity, ioStore.mapId);

      try {
        const createdTask = await this.graphClient
          .api(`/me/todo/lists/${selectedListId}/tasks`)
          .post(taskRequest);

        // Add completion criteria as checklist items if requested
        if (
          includeCriteria &&
          activity.criteria &&
          activity.criteria.length > 0
        ) {
          const criteria = await activityProjectStore.loadCompletionCriteria(
            ioStore.mapId,
            activity.id,
          );

          await addChecklistItemsToTask(
            this.graphClient,
            selectedListId,
            createdTask.id,
            criteria,
          );
        }
      } catch (error) {
        console.error('Failed to create task:', error);
        throw error;
      }
    },

    async exportActivitiesToTodo(
      selectedListId: string,
      activities: MapActivity[],
      includeCriteriaAsChecklist: boolean = true,
    ) {
      const ioStore = useIoStore();
      const activityProjectStore = useActivityProjectStore();

      activityProjectStore.getProject(ioStore.mapId);

      for (const activity of activities) {
        await activityProjectStore.loadActivity(ioStore.mapId, activity.id);

        const criteria = await activityProjectStore.loadCompletionCriteria(
          ioStore.mapId,
          activity.id,
        );

        // Create main task for the activity using helper
        const taskRequest = createActivityTaskRequest(activity, ioStore.mapId);

        try {
          const createdTask = await this.graphClient
            .api(`/me/todo/lists/${selectedListId}/tasks`)
            .post(taskRequest);

          const activeCriteria = criteria.filter(
            (criterion) => criterion.state !== 'done',
          );

          if (includeCriteriaAsChecklist) {
            // Add completion criteria as checklist items using helper
            await addChecklistItemsToTask(
              this.graphClient,
              selectedListId,
              createdTask.id,
              criteria,
            );
          } else {
            // Create separate tasks for each criterion
            let number = activeCriteria.length;
            for (const criterion of criteria.reverse()) {
              // Skip completed criteria
              if ('state' in criterion && criterion.state === 'done') {
                continue;
              }

              // Use helper to create criterion task request
              const criterionTaskRequest = createCriterionTaskRequest(
                criterion,
                activity,
                ioStore.mapId,
                number,
              );

              try {
                await this.graphClient
                  .api(`/me/todo/lists/${selectedListId}/tasks`)
                  .post(criterionTaskRequest);
              } catch (error) {
                console.error('Failed to create criterion task:', error);
              }
              number -= 1;
            }
          }
        } catch (error) {
          console.error('Failed to create task:', error);
          throw error;
        }
      }
    },

    async createTaskInTodo(
      listId: string,
      taskRequest: TodoTaskCreateRequest,
    ): Promise<TodoTask> {
      try {
        const response = await this.graphClient
          .api(`/me/todo/lists/${listId}/tasks`)
          .post(taskRequest);

        return response;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },

    async exportMultipleActivitiesToTodo(
      selectedListId: string,
      activityIds: string[],
      includeCriteriaAsChecklist: boolean = false,
    ) {
      const ioStore = useIoStore();

      // Get activities and filter out completed ones
      const activities = ioStore.activities
        .filter((activity) => activityIds.includes(activity.id))
        .filter((activity) => activity.state !== 'done')
        .sort((a, b) => {
          return b.deadline.localeCompare(a.deadline);
        });

      for (const activity of activities) {
        // Create main task for the activity using helper
        const taskRequest = createActivityTaskRequest(activity, ioStore.mapId);

        try {
          const createdTask = await this.graphClient
            .api(`/me/todo/lists/${selectedListId}/tasks`)
            .post(taskRequest);

          // Only add completion criteria as checklist items if requested
          if (
            includeCriteriaAsChecklist &&
            activity.criteria &&
            activity.criteria.length > 0
          ) {
            await addChecklistItemsToTask(
              this.graphClient,
              selectedListId,
              createdTask.id,
              activity.criteria,
            );
          }
        } catch (error) {
          console.error('Failed to create task:', error);
          throw error;
        }
      }
    },

    async exportSingleTaskToTodo(
      selectedListId: string,
      task: TaskItem,
      isAction: boolean,
      activity: ActivitySummary,
    ) {
      const ioStore = useIoStore();
      const subdomain = getTenant();

      // Create task with activity context
      const taskType = isAction
        ? $t('msTodoIntegration.taskBodyAction')
        : $t('msTodoIntegration.taskBodyCompletionCriterion');
      const taskRequest: TodoTaskCreateRequest = {
        title: shortenString(task.title),
        body: {
          content: `${taskType}\n${$t('msTodoIntegration.taskBodyActivityLabel', { title: activity.title })}\n\n${$t('msTodoIntegration.taskBodyViewInBeeboard', { url: `https://${subdomain}.beeboard.eu/projects/${ioStore.mapId}/activities/${activity.id}` })}`,
          contentType: 'text',
        },
        dueDateTime: task.current_deadline
          ? {
              dateTime: task.current_deadline,
              timeZone: 'UTC',
            }
          : undefined,
        importance: 'normal',
        status: task.state === 'done' ? 'completed' : 'notStarted',
      };

      try {
        await this.graphClient
          .api(`/me/todo/lists/${selectedListId}/tasks`)
          .post(taskRequest);
      } catch (error) {
        console.error('Failed to create task:', error);
        throw error;
      }
    },

    async exportExtractedTasksToTodo(
      selectedListId: string,
      tasks: Array<{
        title: string;
        description?: string | null;
        responsibleName?: string;
        responsibleEmail?: string;
        dueDate?: string | null;
      }>,
      context?: string,
    ) {
      for (const task of tasks) {
        const descriptionText = htmlToPlainText(task.description);
        const taskRequest: TodoTaskCreateRequest = {
          title: shortenString(task.title),
          body: {
            content: `${$t('msTodoIntegration.taskBodyResponsible', { name: task.responsibleName || '', email: task.responsibleEmail || '' })}${descriptionText ? `\n\n${descriptionText}` : ''}${context ? `\n\n${$t('msTodoIntegration.taskBodyContext', { context })}` : ''}`,
            contentType: 'text',
          },
          dueDateTime: task.dueDate
            ? {
                dateTime: `${task.dueDate}T00:00:00.0000000`,
                timeZone: 'UTC',
              }
            : undefined,
          importance: 'normal',
          status: 'notStarted',
        };

        try {
          await this.graphClient
            .api(`/me/todo/lists/${selectedListId}/tasks`)
            .post(taskRequest);
        } catch (error) {
          console.error('Failed to create task:', error);
          throw error;
        }
      }
    },
  },
});
